"use client";

import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Leaf,
  Globe2,
  Microscope,
  Lightbulb,
  Sprout,
  TreePine,
  Waves,
  Wind,
  Sun,
  Recycle,
  Brain,
  Map,
  ShieldCheck,
  Users,
  GraduationCap,
  Landmark,
  Building2,
  Handshake,
  Mail,
  MapPin,
  Phone,
  ExternalLink,
  ChevronRight,
  FlaskConical,
  Target,
  Eye,
  Network,
  BookOpen,
  Presentation,
  Cpu,
  CloudSun,
  Droplets,
} from "lucide-react";

const logo = "/assets/img/bubbanlogo.jpg";

/* -------------------------------------------------------
   Animation Variants
------------------------------------------------------- */

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
};

const fadeLeft = {
  hidden: {
    opacity: 0,
    x: -60,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut",
    },
  },
};

const fadeRight = {
  hidden: {
    opacity: 0,
    x: 60,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut",
    },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

/* -------------------------------------------------------
   Research Themes
------------------------------------------------------- */

const researchThemes = [
  {
    title: "Climate Change & Adaptation",
    icon: CloudSun,
    description:
      "Research on climate risks, adaptation strategies, resilience and climate-smart development.",
  },
  {
    title: "Environmental Sustainability",
    icon: Leaf,
    description:
      "Innovative approaches for sustainable use of natural resources and ecological protection.",
  },
  {
    title: "Biodiversity & Ecosystems",
    icon: TreePine,
    description:
      "Conservation of biodiversity, ecosystems, forests and environmentally sensitive areas.",
  },
  {
    title: "Renewable Energy",
    icon: Sun,
    description:
      "Research and innovation in renewable energy, green technology and clean energy systems.",
  },
  {
    title: "Environmental Governance",
    icon: Landmark,
    description:
      "Evidence-based environmental governance, public policy and institutional development.",
  },
  {
    title: "Disaster Risk Reduction",
    icon: ShieldCheck,
    description:
      "Building resilient communities and reducing environmental and climate-related disaster risks.",
  },
  {
    title: "GIS & Remote Sensing",
    icon: Map,
    description:
      "Geospatial technologies for environmental monitoring, mapping and decision support.",
  },
  {
    title: "AI for Environment",
    icon: Brain,
    description:
      "Artificial intelligence and data-driven technologies for environmental management.",
  },
  {
    title: "Water & Watersheds",
    icon: Droplets,
    description:
      "Sustainable water resources management, watershed protection and community resilience.",
  },
  {
    title: "Circular Economy",
    icon: Recycle,
    description:
      "Waste reduction, resource efficiency, recycling and circular production systems.",
  },
  {
    title: "Sustainable Agriculture",
    icon: Sprout,
    description:
      "Climate-smart agriculture, food systems and environmentally responsible farming.",
  },
  {
    title: "Sustainable Tourism",
    icon: Globe2,
    description:
      "Responsible tourism models that protect natural resources and benefit local communities.",
  },
  {
    title: "Indigenous Knowledge",
    icon: Users,
    description:
      "Community-based development and integration of indigenous environmental knowledge.",
  },
  {
    title: "Smart & Sustainable Cities",
    icon: Building2,
    description:
      "Technology-enabled approaches for resilient, inclusive and sustainable urban development.",
  },
  {
    title: "Digital Innovation",
    icon: Cpu,
    description:
      "Digital technologies and innovative solutions supporting sustainable development.",
  },
];

/* -------------------------------------------------------
   Activities
------------------------------------------------------- */

const activities = [
  {
    title: "Research Projects",
    icon: FlaskConical,
  },
  {
    title: "Innovation & Technology Development",
    icon: Lightbulb,
  },
  {
    title: "Conferences, Seminars & Workshops",
    icon: Presentation,
  },
  {
    title: "Professional Training",
    icon: GraduationCap,
  },
  {
    title: "Student Research Support",
    icon: BookOpen,
  },
  {
    title: "Policy Dialogue & Advocacy",
    icon: Landmark,
  },
  {
    title: "Publications & Knowledge Sharing",
    icon: BookOpen,
  },
  {
    title: "Community Engagement",
    icon: Users,
  },
  {
    title: "Collaborative Research",
    icon: Network,
  },
  {
    title: "Consultancy & Technical Services",
    icon: Handshake,
  },
];

/* -------------------------------------------------------
   Collaboration
------------------------------------------------------- */

const collaborations = [
  {
    title: "Universities & Research Institutes",
    icon: GraduationCap,
  },
  {
    title: "Government Agencies",
    icon: Landmark,
  },
  {
    title: "International Organizations",
    icon: Globe2,
  },
  {
    title: "Development Partners",
    icon: Handshake,
  },
  {
    title: "NGOs",
    icon: Users,
  },
  {
    title: "Industry & Private Sector",
    icon: Building2,
  },
  {
    title: "Professional Networks",
    icon: Network,
  },
  {
    title: "Community Organizations",
    icon: Sprout,
  },
];

/* -------------------------------------------------------
   Floating Background Element
------------------------------------------------------- */

function FloatingLeaf({ className = "", delay = 0 }) {
  return (
    <motion.div
      className={`absolute pointer-events-none ${className}`}
      animate={{
        y: [0, -15, 0],
        rotate: [0, 5, -5, 0],
      }}
      transition={{
        duration: 5,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <Leaf className="w-10 h-10 text-white/10" />
    </motion.div>
  );
}

/* -------------------------------------------------------
   Section Heading
------------------------------------------------------- */

function SectionHeading({
  eyebrow,
  title,
  description,
  light = false,
}) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      className="max-w-3xl mx-auto text-center mb-14"
    >
      <div
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-5 ${
          light
            ? "bg-white/10 text-[#F2B233] border border-white/10"
            : "bg-[#EAF7F0] text-[#075C35]"
        }`}
      >
        <span className="w-2 h-2 rounded-full bg-[#F2B233] animate-pulse" />
        {eyebrow}
      </div>

      <h2
        className={`text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight ${
          light ? "text-white" : "text-[#063A25]"
        }`}
      >
        {title}
      </h2>

      {description && (
        <p
          className={`mt-5 text-base md:text-lg leading-8 ${
            light ? "text-white/70" : "text-gray-600"
          }`}
        >
          {description}
        </p>
      )}
    </motion.div>
  );
}

/* -------------------------------------------------------
   Main Component
------------------------------------------------------- */

export default function CERTI() {
  return (
    <main className="overflow-hidden bg-white text-gray-800">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="relative min-h-[720px] lg:min-h-[790px] flex items-center overflow-hidden bg-[#063A25]">

        {/* Background gradients */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(242,178,51,0.18),transparent_28%),radial-gradient(circle_at_80%_70%,rgba(22,131,75,0.35),transparent_35%)]" />

        {/* Decorative circles */}
        <motion.div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full border border-[#F2B233]/10"
          animate={{ rotate: 360 }}
          transition={{
            duration: 35,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        <motion.div
          className="absolute -bottom-52 -left-52 w-[650px] h-[650px] rounded-full border border-white/5"
          animate={{ rotate: -360 }}
          transition={{
            duration: 45,
            repeat: Infinity,
            ease: "linear",
          }}
        />

        {/* Floating leaves */}
        <FloatingLeaf className="top-32 left-[8%]" delay={0} />
        <FloatingLeaf className="top-[25%] right-[12%]" delay={1} />
        <FloatingLeaf className="bottom-28 left-[18%]" delay={2} />
        <FloatingLeaf className="bottom-20 right-[25%]" delay={3} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 w-full">

          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-20 items-center">

            {/* Hero Content */}

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              animate="visible"
            >

              {/* University badge */}
              <div className="flex items-center gap-4 mb-8">

                <div className="w-16 h-16 rounded-2xl bg-white p-1.5 shadow-2xl">
                  <img
                    src={logo}
                    alt="Bandarban University"
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>

                <div>
                  <p className="text-[#F2B233] text-xs uppercase tracking-[0.25em] font-bold">
                    Bandarban University
                  </p>

                  <p className="text-white/60 text-sm mt-1">
                    Department of Governance and Development Studies
                  </p>
                </div>

              </div>

              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F2B233]/30 bg-[#F2B233]/10 text-[#F2B233] text-sm font-semibold mb-6">
                <Leaf className="w-4 h-4" />
                Research • Innovation • Sustainability
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.98] tracking-tight">
                Center for
                <span className="block text-[#F2B233]">
                  Environmental Research
                </span>
                <span className="block">
                  and Technological Innovation
                </span>
              </h1>

              <div className="mt-6 flex items-center gap-3">
                <div className="h-1 w-16 bg-[#F2B233] rounded-full" />
                <span className="text-white/70 font-semibold tracking-[0.3em] text-sm">
                  CERTI
                </span>
              </div>

              <p className="mt-7 text-lg md:text-xl leading-8 text-white/70 max-w-2xl">
                Advancing environmental research, technological innovation,
                sustainable development and evidence-based policy solutions
                from the ecologically rich Chattogram Hill Tracts.
              </p>

              <div className="mt-9 flex flex-wrap gap-4">

                <a
                  href="#research"
                  className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#F2B233] text-[#063A25] font-bold shadow-xl shadow-black/20 hover:bg-[#ffc95c] transition"
                >
                  Explore Research
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>

                <a
                  href="#contact"
                  className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/20 bg-white/5 text-white font-semibold hover:bg-white/10 transition"
                >
                  Contact CERTI
                </a>

              </div>

              {/* Quick stats */}

              <div className="grid grid-cols-3 gap-5 mt-12 max-w-xl">

                <div>
                  <div className="text-3xl font-black text-white">
                    15+
                  </div>
                  <div className="text-xs text-white/50 mt-1">
                    Research Themes
                  </div>
                </div>

                <div>
                  <div className="text-3xl font-black text-white">
                    10+
                  </div>
                  <div className="text-xs text-white/50 mt-1">
                    Core Activities
                  </div>
                </div>

                <div>
                  <div className="text-3xl font-black text-[#F2B233]">
                    CHT
                  </div>
                  <div className="text-xs text-white/50 mt-1">
                    Living Laboratory
                  </div>
                </div>

              </div>

            </motion.div>

            {/* Hero Visual */}

            <motion.div
              variants={fadeRight}
              initial="hidden"
              animate="visible"
              className="relative hidden md:block"
            >

              {/* Outer ring */}

              <motion.div
                className="absolute inset-[-25px] rounded-full border border-[#F2B233]/20"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 25,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              <motion.div
                className="absolute inset-[-55px] rounded-full border border-white/5"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 35,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              {/* Main visual */}

              <div className="relative w-[390px] h-[390px] lg:w-[470px] lg:h-[470px] mx-auto">

                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#16834B] to-[#063A25] shadow-[0_30px_100px_rgba(0,0,0,0.35)]" />

                <div className="absolute inset-8 rounded-full border border-[#F2B233]/30" />

                <div className="absolute inset-16 rounded-full bg-[#075C35]/70 backdrop-blur-sm flex items-center justify-center">

                  <motion.div
                    animate={{
                      scale: [1, 1.06, 1],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                    }}
                    className="relative text-center"
                  >

                    <div className="w-28 h-28 lg:w-36 lg:h-36 mx-auto rounded-full bg-white p-3 shadow-2xl">
                      <img
                        src="/assets/img/certi.png"
                        alt="Bandarban University Logo"
                        className="w-full h-full object-cover rounded-full"
                      />
                    </div>

                    <p className="text-[#F2B233] font-black text-4xl mt-6">
                      CERTI
                    </p>

                    <p className="text-white/60 text-xs uppercase tracking-[0.25em] mt-2">
                      Research & Innovation
                    </p>

                  </motion.div>

                </div>

                {/* Floating icon cards */}

                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                  className="absolute -top-3 right-2 lg:right-0 bg-white rounded-2xl p-4 shadow-2xl"
                >
                  <Leaf className="w-7 h-7 text-[#16834B]" />
                </motion.div>

                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{
                    duration: 4.5,
                    repeat: Infinity,
                  }}
                  className="absolute bottom-6 -left-5 bg-white rounded-2xl p-4 shadow-2xl"
                >
                  <Microscope className="w-7 h-7 text-[#075C35]" />
                </motion.div>

                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                  }}
                  className="absolute bottom-20 -right-8 bg-[#F2B233] rounded-2xl p-4 shadow-2xl"
                >
                  <Lightbulb className="w-7 h-7 text-[#063A25]" />
                </motion.div>

              </div>

            </motion.div>

          </div>

        </div>

        {/* Bottom wave */}

        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 120"
            className="w-full h-auto"
            preserveAspectRatio="none"
          >
            <path
              d="M0,80 C240,140 420,0 720,50 C1000,100 1180,20 1440,65 L1440,120 L0,120 Z"
              fill="#ffffff"
            />
          </svg>
        </div>

      </section>

      {/* =====================================================
          OVERVIEW
      ===================================================== */}

      <section className="relative py-24 bg-white">

        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <div className="grid lg:grid-cols-2 gap-14 items-center">

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
            >

              <div className="inline-flex items-center gap-2 text-[#075C35] font-bold uppercase tracking-widest text-sm mb-5">
                <span className="w-8 h-[2px] bg-[#F2B233]" />
                About CERTI
              </div>

              <h2 className="text-4xl md:text-5xl font-black text-[#063A25] leading-tight">
                Research that connects
                <span className="text-[#16834B]"> people, planet</span>
                <span> and technology.</span>
              </h2>

              <p className="mt-7 text-gray-600 leading-8 text-lg">
                The Center for Environmental Research and Technological
                Innovation (CERTI) is a multidisciplinary research centre
                established under the Department of Governance and Development
                Studies (GDS), Bandarban University.
              </p>

              <p className="mt-5 text-gray-600 leading-8">
                The Centre promotes research, innovation, knowledge exchange,
                and evidence-based policy solutions to address environmental
                and sustainable development challenges at local, national and
                global levels.
              </p>

              <p className="mt-5 text-gray-600 leading-8">
                Situated in the ecologically rich Chattogram Hill Tracts,
                CERTI creates a platform where researchers, faculty members,
                students, development practitioners, policymakers, industries
                and communities can work together.
              </p>

            </motion.div>

            <motion.div
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              className="relative"
            >

              <div className="absolute -inset-5 bg-[#EAF7F0] rounded-[2.5rem] rotate-3" />

              <div className="relative bg-[#075C35] rounded-[2rem] p-8 md:p-10 overflow-hidden shadow-2xl">

                <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-[#F2B233]/10" />

                <div className="absolute -left-20 -bottom-20 w-56 h-56 rounded-full bg-white/5" />

                <div className="relative">

                  <div className="w-16 h-16 rounded-2xl bg-[#F2B233] flex items-center justify-center mb-7">
                    <Globe2 className="w-8 h-8 text-[#063A25]" />
                  </div>

                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    A Living Laboratory
                  </h3>

                  <p className="mt-5 text-white/70 leading-8">
                    The Chattogram Hill Tracts provide a unique ecological
                    and socio-cultural setting for research on climate,
                    biodiversity, indigenous knowledge, natural resources,
                    sustainable development and community resilience.
                  </p>

                  <div className="mt-8 grid grid-cols-2 gap-4">

                    {[
                      ["Climate", CloudSun],
                      ["Biodiversity", TreePine],
                      ["Communities", Users],
                      ["Technology", Cpu],
                    ].map(([text, Icon]) => (
                      <div
                        key={text}
                        className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 p-4"
                      >
                        <Icon className="w-5 h-5 text-[#F2B233]" />
                        <span className="text-sm text-white/80 font-medium">
                          {text}
                        </span>
                      </div>
                    ))}

                  </div>

                </div>

              </div>

            </motion.div>

          </div>

        </div>

      </section>

      {/* =====================================================
          VISION & MISSION
      ===================================================== */}

      <section className="relative py-24 bg-[#F4FAF6]">

        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <SectionHeading
            eyebrow="Our Direction"
            title="Vision & Mission"
            description="Building knowledge, strengthening resilience and transforming research into meaningful environmental and development impact."
          />

          <div className="grid md:grid-cols-2 gap-8">

            {/* Vision */}

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="relative group overflow-hidden rounded-[2rem] bg-[#075C35] p-8 md:p-10 shadow-xl"
            >

              <div className="absolute -right-20 -top-20 w-56 h-56 rounded-full bg-[#F2B233]/10" />

              <div className="relative">

                <div className="w-16 h-16 rounded-2xl bg-[#F2B233] flex items-center justify-center">
                  <Eye className="w-8 h-8 text-[#063A25]" />
                </div>

                <p className="mt-8 text-[#F2B233] uppercase tracking-[0.2em] text-sm font-bold">
                  Vision
                </p>

                <h3 className="mt-3 text-3xl font-black text-white">
                  A Centre of Excellence
                </h3>

                <p className="mt-6 text-white/75 leading-8 text-lg">
                  To become a leading centre of excellence in environmental
                  research, technological innovation, and sustainable
                  development through impactful research, collaboration, and
                  innovation.
                </p>

              </div>

            </motion.div>

            {/* Mission */}

            <motion.div
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="relative overflow-hidden rounded-[2rem] bg-white p-8 md:p-10 border border-[#D9EDE1] shadow-xl"
            >

              <div className="absolute -right-20 -top-20 w-56 h-56 rounded-full bg-[#EAF7F0]" />

              <div className="relative">

                <div className="w-16 h-16 rounded-2xl bg-[#EAF7F0] flex items-center justify-center">
                  <Target className="w-8 h-8 text-[#075C35]" />
                </div>

                <p className="mt-8 text-[#16834B] uppercase tracking-[0.2em] text-sm font-bold">
                  Mission
                </p>

                <div className="mt-5 space-y-3">

                  {[
                    "Advance interdisciplinary environmental research.",
                    "Promote innovation and technology for sustainable development.",
                    "Strengthen climate resilience and environmental governance.",
                    "Support evidence-based policymaking.",
                    "Build research capacity among students and professionals.",
                    "Foster national and international academic collaboration.",
                    "Engage communities through research and outreach.",
                  ].map((item, index) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: index * 0.05,
                      }}
                      className="flex gap-3 items-start"
                    >
                      <div className="mt-1 w-5 h-5 shrink-0 rounded-full bg-[#EAF7F0] flex items-center justify-center">
                        <ChevronRight className="w-3.5 h-3.5 text-[#16834B]" />
                      </div>

                      <span className="text-gray-600 leading-6">
                        {item}
                      </span>
                    </motion.div>
                  ))}

                </div>

              </div>

            </motion.div>

          </div>

        </div>

      </section>

      {/* =====================================================
          RESEARCH THEMES
      ===================================================== */}

      <section
        id="research"
        className="relative py-24 bg-white"
      >

        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <SectionHeading
            eyebrow="Research & Innovation"
            title="Research Themes"
            description="CERTI brings together interdisciplinary expertise to address complex environmental, technological and sustainable development challenges."
          />

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.05 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >

            {researchThemes.map((theme, index) => {

              const Icon = theme.icon;

              return (
                <motion.div
                  key={theme.title}
                  variants={fadeUp}
                  whileHover={{
                    y: -7,
                    transition: { duration: 0.2 },
                  }}
                  className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-xl hover:border-[#BFE2CF] transition-all"
                >

                  <div className="absolute right-0 top-0 w-20 h-20 rounded-bl-[4rem] bg-[#F4FAF6] group-hover:bg-[#EAF7F0] transition-colors" />

                  <div className="relative">

                    <div className="w-12 h-12 rounded-xl bg-[#EAF7F0] flex items-center justify-center group-hover:bg-[#075C35] transition-colors">
                      <Icon className="w-6 h-6 text-[#075C35] group-hover:text-[#F2B233] transition-colors" />
                    </div>

                    <h3 className="mt-5 font-bold text-lg text-[#063A25] group-hover:text-[#075C35] transition-colors">
                      {theme.title}
                    </h3>

                    <p className="mt-3 text-sm text-gray-500 leading-6">
                      {theme.description}
                    </p>

                    <div className="mt-5 flex items-center gap-1 text-[#16834B] text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                      Explore theme
                      <ArrowRight className="w-4 h-4" />
                    </div>

                  </div>

                </motion.div>
              );
            })}

          </motion.div>

        </div>

      </section>

      {/* =====================================================
          ACTIVITIES
      ===================================================== */}

      <section className="relative py-24 bg-[#063A25] overflow-hidden">

        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(242,178,51,0.12),transparent_30%),radial-gradient(circle_at_10%_90%,rgba(22,131,75,0.25),transparent_35%)]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">

          <SectionHeading
            light
            eyebrow="What We Do"
            title="CERTI Activities"
            description="From research and innovation to policy dialogue and community engagement, CERTI connects knowledge with action."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">

            {activities.map((activity, index) => {

              const Icon = activity.icon;

              return (
                <motion.div
                  key={activity.title}
                  initial={{
                    opacity: 0,
                    y: 25,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{ once: true }}
                  transition={{
                    delay: index * 0.06,
                  }}
                  whileHover={{
                    y: -7,
                  }}
                  className="group rounded-2xl bg-white/5 border border-white/10 hover:border-[#F2B233]/40 hover:bg-white/10 p-6 transition-all"
                >

                  <div className="w-12 h-12 rounded-xl bg-[#F2B233] flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#063A25]" />
                  </div>

                  <h3 className="mt-5 text-white font-bold leading-6">
                    {activity.title}
                  </h3>

                  <div className="mt-5 w-7 h-[2px] bg-[#F2B233] group-hover:w-12 transition-all" />

                </motion.div>
              );
            })}

          </div>

        </div>

      </section>

      {/* =====================================================
          COLLABORATION
      ===================================================== */}

      <section className="relative py-24 bg-[#F4FAF6]">

        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <SectionHeading
            eyebrow="Work With Us"
            title="Collaboration for Impact"
            description="CERTI welcomes partnerships that strengthen research excellence, innovation, policy impact and sustainable development."
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">

            {collaborations.map((item, index) => {

              const Icon = item.icon;

              return (
                <motion.div
                  key={item.title}
                  initial={{
                    opacity: 0,
                    scale: 0.95,
                  }}
                  whileInView={{
                    opacity: 1,
                    scale: 1,
                  }}
                  viewport={{ once: true }}
                  transition={{
                    delay: index * 0.06,
                  }}
                  whileHover={{
                    scale: 1.025,
                  }}
                  className="group bg-white rounded-2xl p-6 border border-[#DCEDE3] shadow-sm hover:shadow-lg transition-all"
                >

                  <div className="flex items-center gap-4">

                    <div className="w-12 h-12 shrink-0 rounded-xl bg-[#EAF7F0] flex items-center justify-center group-hover:bg-[#075C35] transition-colors">
                      <Icon className="w-6 h-6 text-[#075C35] group-hover:text-[#F2B233] transition-colors" />
                    </div>

                    <h3 className="font-bold text-[#063A25] leading-6">
                      {item.title}
                    </h3>

                  </div>

                </motion.div>
              );
            })}

          </div>

          {/* Collaboration CTA */}

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mt-14 relative overflow-hidden rounded-[2rem] bg-[#075C35] p-8 md:p-12"
          >

            <div className="absolute right-0 top-0 w-80 h-80 rounded-full border-[40px] border-[#F2B233]/5 translate-x-1/3 -translate-y-1/3" />

            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">

              <div className="max-w-2xl">

                <div className="flex items-center gap-3">
                  <Handshake className="w-6 h-6 text-[#F2B233]" />

                  <span className="text-[#F2B233] uppercase tracking-widest text-sm font-bold">
                    Let's Collaborate
                  </span>
                </div>

                <h3 className="mt-4 text-3xl md:text-4xl font-black text-white">
                  Turn research into real-world impact.
                </h3>

                <p className="mt-4 text-white/70 leading-7">
                  Join CERTI in building partnerships that create practical,
                  innovative and sustainable solutions for people and the
                  planet.
                </p>

              </div>

              <a
                href="#contact"
                className="shrink-0 inline-flex items-center justify-center gap-3 px-7 py-3.5 rounded-full bg-[#F2B233] text-[#063A25] font-bold hover:bg-[#ffc95c] transition"
              >
                Start a Collaboration
                <ArrowRight className="w-5 h-5" />
              </a>

            </div>

          </motion.div>

        </div>

      </section>

      {/* =====================================================
          CONTACT
      ===================================================== */}

      <section
        id="contact"
        className="relative py-24 bg-white"
      >

        <div className="max-w-7xl mx-auto px-6 lg:px-8">

          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10">

            {/* Contact Intro */}

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-[2rem] bg-[#063A25] p-8 md:p-10"
            >

              <div className="absolute -right-24 -bottom-24 w-72 h-72 rounded-full border-[35px] border-[#F2B233]/10" />

              <div className="relative">

                <div className="w-16 h-16 rounded-2xl bg-[#F2B233] flex items-center justify-center">
                  <Leaf className="w-8 h-8 text-[#063A25]" />
                </div>

                <p className="mt-8 text-[#F2B233] text-sm font-bold uppercase tracking-[0.2em]">
                  Contact CERTI
                </p>

                <h2 className="mt-3 text-3xl md:text-4xl font-black text-white">
                  Center for Environmental Research and Technological Innovation
                </h2>

                <p className="mt-5 text-white/65 leading-7">
                  Department of Governance and Development Studies (GDS)
                  <br />
                  Bandarban University
                </p>

                <div className="mt-10 space-y-6">

                  <div className="flex gap-4">

                    <div className="w-11 h-11 shrink-0 rounded-xl bg-white/10 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#F2B233]" />
                    </div>

                    <div>
                      <p className="text-white/40 text-xs uppercase tracking-wider">
                        Address
                      </p>

                      <p className="mt-1 text-white/80 leading-6">
                        KB Road, Sualok,
                        <br />
                        Bandarban Sadar - 4600,
                        <br />
                        Bandarban, Bangladesh
                      </p>
                    </div>

                  </div>

                  <div className="flex gap-4">

                    <div className="w-11 h-11 shrink-0 rounded-xl bg-white/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#F2B233]" />
                    </div>

                    <div>
                      <p className="text-white/40 text-xs uppercase tracking-wider">
                        Email
                      </p>

                      <a
                        href="mailto:certi@bubban.ac.bd"
                        className="mt-1 inline-block text-white hover:text-[#F2B233] transition"
                      >
                        certi@bubban.ac.bd
                      </a>
                    </div>

                  </div>

                </div>

              </div>

            </motion.div>

            {/* Contact Card */}

            <motion.div
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="rounded-[2rem] border border-gray-100 shadow-xl p-8 md:p-10"
            >

              <div className="flex items-center gap-4 mb-8">

                <div className="w-14 h-14 rounded-2xl bg-[#EAF7F0] flex items-center justify-center">
                  <Network className="w-7 h-7 text-[#075C35]" />
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-[#063A25]">
                    Connect with CERTI
                  </h3>

                  <p className="text-gray-500 text-sm mt-1">
                    Research • Innovation • Collaboration
                  </p>
                </div>

              </div>

              <div className="grid sm:grid-cols-2 gap-5">

                {[
                  {
                    icon: FlaskConical,
                    title: "Research",
                    text: "Collaborate on interdisciplinary environmental research.",
                  },
                  {
                    icon: Lightbulb,
                    title: "Innovation",
                    text: "Develop practical technology-driven solutions.",
                  },
                  {
                    icon: Users,
                    title: "Community",
                    text: "Engage communities through research and outreach.",
                  },
                  {
                    icon: Globe2,
                    title: "Global Partnership",
                    text: "Build national and international research networks.",
                  },
                ].map((item) => {

                  const Icon = item.icon;

                  return (
                    <div
                      key={item.title}
                      className="rounded-2xl bg-[#F7FBF8] border border-[#E3F0E8] p-5"
                    >

                      <Icon className="w-6 h-6 text-[#16834B]" />

                      <h4 className="mt-4 font-bold text-[#063A25]">
                        {item.title}
                      </h4>

                      <p className="mt-2 text-sm text-gray-500 leading-6">
                        {item.text}
                      </p>

                    </div>
                  );
                })}

              </div>

              <div className="mt-8 p-5 rounded-2xl bg-[#EAF7F0] border border-[#D2EBDD]">

                <p className="text-[#075C35] font-semibold">
                  Center for Environmental Research and Technological
                  Innovation (CERTI)
                </p>

                <p className="mt-2 text-gray-600 text-sm">
                  Department of Governance and Development Studies (GDS),
                  Bandarban University
                </p>

              </div>

              <a
                href="mailto:certi@bubban.ac.bd"
                className="mt-6 inline-flex items-center gap-2 text-[#075C35] font-bold hover:text-[#16834B] transition"
              >
                Send an Email
                <ExternalLink className="w-4 h-4" />
              </a>

            </motion.div>

          </div>

        </div>

      </section>

      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <section className="relative overflow-hidden bg-[#F2B233]">

        <div className="absolute inset-0 opacity-20">
          <div className="absolute w-80 h-80 rounded-full border-[50px] border-[#063A25] -left-20 -top-40" />
          <div className="absolute w-96 h-96 rounded-full border-[60px] border-[#063A25] -right-40 -bottom-60" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 py-20 text-center">

          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{ once: true }}
          >

            <Leaf className="w-10 h-10 mx-auto text-[#063A25]" />

            <h2 className="mt-5 text-4xl md:text-5xl font-black text-[#063A25]">
              Building a Sustainable Future Together
            </h2>

            <p className="mt-5 max-w-2xl mx-auto text-[#063A25]/70 text-lg leading-8">
              Through research, innovation, collaboration and community
              engagement, CERTI aims to create knowledge and solutions for a
              more resilient and sustainable future.
            </p>

            <a
              href="mailto:certi@bubban.ac.bd"
              className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#063A25] text-white font-bold hover:bg-[#075C35] transition shadow-xl"
            >
              Connect with CERTI
              <ArrowRight className="w-5 h-5" />
            </a>

          </motion.div>

        </div>

      </section>

    </main>
  );
}